import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  OnChanges,
} from '@angular/core';
import * as d3 from 'd3';
import { BrushBehavior, ScaleTime, Selection } from 'd3';
import * as moment from 'moment';
import { Subject, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DateRange } from '../date-range';

interface HandleType {
  type: string;
}

type D3SVGElement = Selection<SVGGElement, unknown, null, undefined>;

@Component({
    selector: 'app-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class DateRangePickerComponent implements AfterViewInit, OnChanges {
  @ViewChild('svgElement') svgElement!: ElementRef;

  @Input()
  bounds!: DateRange;

  @Input()
  minSelectionCount = 1;

  @Input()
  maxSelectionCount?: number;

  @Input()
  selectRange$?: Observable<DateRange>;

  @Output()
  rangeChanged = new EventEmitter<DateRange>();

  maxFromDateValue?: Date;

  debouncer: Subject<DateRange> = new Subject();
  selectedRange?: DateRange;

  x!: ScaleTime<number, number>;
  width = 900;

  height = 75;
  margin = { top: 10, right: 20, bottom: 20, left: 20 };
  svg!: D3SVGElement;
  svgBrush!: D3SVGElement;
  brush!: BrushBehavior<unknown>;
  arc = d3
    .arc()
    .innerRadius(0)
    .outerRadius((this.height - this.margin.top - this.margin.bottom) / 3)
    .startAngle(0)
    .endAngle((d, i) => (i ? Math.PI : -Math.PI));
  interval = d3.timeMonth.every(1);
  firstDraw = true;

  constructor() {
    this.debouncer.pipe(debounceTime(100)).subscribe((value) => {
      const fromDate = this.convertDateToCST(value.fromDate).toDate();

      const toDate = this.convertDateToCST(value.toDate)
        .subtract(1, 'millisecond')
        .toDate();
      this.rangeChanged.emit({
        fromDate,
        toDate,
      });
    });
  }

  private convertDateToCST(date: Date): moment.Moment {
    const dayOfMonth = date.getDate();
    const monthOfYear = date.getMonth() + 1;
    const fullYear = date.getFullYear();
    const dateString = `${dayOfMonth}/${monthOfYear}/${fullYear}`;
    const momented = moment(dateString, 'DD/MM/YYYY', 'America/Chicago');
    return momented;
  }

  ngAfterViewInit() {
    if (!this.bounds) {
      throw new Error('bounds was not bound');
    }
    this.createDateRangePicker();
    this.firstDraw = false;
    this.selectRange$?.subscribe(this.setBrushSelection);
  }

  ngOnChanges() {
    if (!this.firstDraw) {
      d3.select('g').remove();
      this.createDateRangePicker();
    }

    this.maxFromDateValue = moment(this.bounds.toDate)
      .subtract(this.minSelectionCount, 'months')
      .toDate();
  }

  createDateRangePicker() {
    this.x = d3
      .scaleTime()
      .domain([this.bounds.fromDate, this.bounds.toDate])
      .rangeRound([this.margin.left, this.width - this.margin.right]);
    this.initSvg();
    this.createAxisX();
    this.createBrush();
  }

  initSvg() {
    this.svg = d3
      .select(this.svgElement.nativeElement)
      .attr(
        'viewBox',
        `0 0 ${this.width + this.margin.left + this.margin.right} ${
          this.height + this.margin.top + this.margin.bottom
        }`,
      )
      .append('g')
      .attr('class', 'wrapper')
      .attr(
        'transform',
        'translate(' + this.margin.left + ',' + this.margin.top + ')',
      );
  }

  createAxisX() {
    this.svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${this.height - this.margin.bottom})`)
      .call(this.createXAxisTicks)
      .call(this.createXAxisText)
      .call(this.styleXAxisText);
  }

  createXAxisTicks = (xAxis: D3SVGElement) => {
    return xAxis
      .append('g')
      .attr('class', 'x-axis-ticks')
      .call(
        d3
          .axisBottom(this.x)
          .ticks(this.interval)
          .tickSize(-this.height + this.margin.top + this.margin.bottom)
          .tickFormat(() => ''),
      );
  };

  shouldTickBeYear(date: Date) {
    const dateAsMoment = moment(date);
    return dateAsMoment.get('month') === 0;
  }

  getTickInterval = () => {
    let monthsBetween =
      (this.bounds.toDate.getFullYear() - this.bounds.fromDate.getFullYear()) *
      12;
    monthsBetween -= this.bounds.fromDate.getMonth();
    monthsBetween += this.bounds.toDate.getMonth();
    if (monthsBetween <= 12) {
      return 1;
    } else if (monthsBetween <= 24) {
      return 2;
    } else {
      return 3;
    }
  };

  createXAxisText = (xAxis: D3SVGElement) => {
    return xAxis
      .append('g')
      .attr('class', 'x-axis-text')
      .call(
        d3
          .axisBottom(this.x)
          .ticks(d3.timeMonth.every(this.getTickInterval()))
          .tickPadding(0)
          .tickFormat((date) => {
            const dateObj = date as Date;
            if (this.shouldTickBeYear(dateObj)) {
              return dateObj.getFullYear().toString();
            }
            return dateObj.toLocaleString('default', { month: 'short' });
          }),
      )
      .attr('text-anchor', null)
      .call((g2) => g2.select('.domain').remove())
      .call((g2) => g2.selectAll('text').attr('x', 6));
  };

  styleXAxisText = (xAxis: D3SVGElement) => {
    xAxis
      .select('.x-axis-text')
      .selectAll('.tick')
      .style('font-weight', (data) => {
        const dateObj = data as Date;
        if (this.shouldTickBeYear(dateObj)) {
          return 'bold';
        }
        return 'normal';
      });
  };

  createBrush() {
    const self = this;
    this.svgBrush = this.svg
      .append('g')
      .attr('class', 'brush')
      .call(
        (self.brush = d3
          .brushX()
          .extent([
            [this.margin.left, this.margin.top],
            [this.width - this.margin.right, this.height - this.margin.bottom],
          ])

          // draws the brush and handles on start and drag of the mouse
          .on('start brush', function (event) {
            // If the sourceEvent is undefined, we're reentering into here from an event we generated
            // ourselves rather than a user event due to interacting with the brush. Eject rather than
            // getting into a loop here.
            if (event?.type === 'brush' && event.sourceEvent === undefined) {
              return;
            }

            const d0 = event.selection.map(self.x.invert);
            const d1 = d0.map(d3.timeMonth.round);

            // Basically, on draw start enforce min selection and max upper bound range.
            if (d1[0] >= d1[1]) {
              // If selection is past maxFromDateValue,
              // depending on minSelectionCount, automatically select the max upper range.
              if (moment(d0[0]).isAfter(self.maxFromDateValue)) {
                d1[0] = self.maxFromDateValue;
                d1[1] = d3.timeMonth.ceil(self.bounds.toDate);
              } else {
                d1[0] = d3.timeMonth.floor(d0[0]);
                // Assuming Offset is based on an interval of 1.
                d1[1] = d3.timeMonth.offset(d1[0], self.minSelectionCount);
              }
            }

            const selectedMonthsCount = d3.timeMonth.count(d1[0], d1[1]);
            // Enforce min and max selection when dragging.
            if (selectedMonthsCount < self.minSelectionCount) {
              // Always enforce min selection when dragging.
              d1[1] = d3.timeMonth.offset(d1[0], self.minSelectionCount);
            } else if (
              self.maxSelectionCount &&
              selectedMonthsCount > self.maxSelectionCount
            ) {
              d1[1] = d3.timeMonth.offset(d1[0], self.maxSelectionCount);
            }

            d3.select(this).call(self.brushHandle.bind(self), d1.map(self.x));
            // Snapping
            d3.select(this).call(self.brush.move, d1.map(self.x));
            self.selectedRange = {
              fromDate: d1[0],
              toDate: d1[1],
            };

            self.debouncer.next(self.selectedRange);
          })),
      );

    if (this.selectedRange) {
      this.setBrushSelection(this.selectedRange);
    }
  }

  setBrushSelection = (dateRange: DateRange) => {
    const firstDateInGraphsRange = this.x.invert(this.margin.left);
    const lastDateInGraphsRange = this.x.invert(this.width - this.margin.right);
    if (dateRange.fromDate < firstDateInGraphsRange) {
      console.warn(
        'Selelected Date expands past bounds. Resetting to bounds instead',
      );
      dateRange.fromDate = firstDateInGraphsRange;
    }
    if (dateRange.toDate > lastDateInGraphsRange) {
      console.warn(
        'Selelected Date expands past bounds. Resetting to bounds instead',
      );
      dateRange.toDate = lastDateInGraphsRange;
    }
    this.svgBrush.call(this.brush.move, [
      this.x(dateRange.fromDate.getTime()),
      this.x(dateRange.toDate.getTime()),
    ]);
  };

  brushHandle(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    selection: Array<number>,
  ) {
    g.selectAll('.handle--custom')
      .data([{ type: 'w' }, { type: 'e' }])
      .join((enter: any) =>
        enter
          .append('path')
          .attr('class', 'handle--custom')
          .attr('cursor', 'ew-resize')
          .attr('d', this.arc.bind(this)),
      )
      .attr('display', () => (selection === null ? 'none' : null))
      .attr('transform', (d: HandleType, i: number) => {
        if (selection === null) {
          return null;
        }
        return `translate(${selection[i]},${
          (this.height + this.margin.top - this.margin.bottom) / 2
        })`;
      });
  }
}
